// Image SEO optimization utilities

export interface ImageSEOData {
  src: string;
  alt: string;
  title?: string;
  caption?: string;
  filename: string;
  width?: number;
  height?: number;
  format: string;
  fileSize?: number;
  keywords: string[];
  context: string;
}

export interface ImageSEOAnalysis {
  score: number;
  issues: string[];
  recommendations: string[];
  optimizedAlt: string;
  optimizedTitle: string;
  optimizedFilename: string;
}

export interface ImageSitemapEntry {
  loc: string;
  image: {
    loc: string;
    caption?: string;
    title?: string;
    license?: string;
    geo_location?: string;
  }[];
}

/**
 * Generate SEO-optimized alt text for images
 */
export const generateOptimizedAltText = (
  image: Partial<ImageSEOData>,
  context: string,
  targetKeywords: string[] = []
): string => {
  const { filename = '', caption = '', title = '' } = image;
  
  // Start with existing alt text or derive from filename
  let altText = image.alt || deriveAltFromFilename(filename);
  
  // If no alt text, use caption or title
  if (!altText) {
    altText = caption || title || '';
  }
  
  // Clean and optimize the alt text
  altText = cleanAltText(altText);
  
  // Add context if relevant
  if (context && !altText.toLowerCase().includes(context.toLowerCase())) {
    const contextWords = extractRelevantWords(context, 3);
    if (contextWords.length > 0) {
      altText = `${altText} ${contextWords.join(' ')}`.trim();
    }
  }
  
  // Include primary keyword if not present
  const primaryKeyword = targetKeywords[0];
  if (primaryKeyword && !altText.toLowerCase().includes(primaryKeyword.toLowerCase())) {
    altText = `${altText} ${primaryKeyword}`.trim();
  }
  
  // Ensure proper length (125 characters max for screen readers)
  if (altText.length > 125) {
    altText = altText.substring(0, 122) + '...';
  }
  
  return altText || 'Image';
};

/**
 * Derive alt text from filename
 */
const deriveAltFromFilename = (filename: string): string => {
  return filename
    .replace(/\.[^/.]+$/, '') // Remove extension
    .replace(/[-_]/g, ' ') // Replace hyphens and underscores with spaces
    .replace(/\b\w/g, l => l.toUpperCase()) // Capitalize first letter of each word
    .trim();
};

/**
 * Clean and optimize alt text
 */
const cleanAltText = (altText: string): string => {
  return altText
    .replace(/^(image of|picture of|photo of|screenshot of)\s*/i, '') // Remove redundant prefixes
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
};

/**
 * Extract relevant words from context
 */
const extractRelevantWords = (context: string, maxWords: number): string[] => {
  const stopWords = new Set([
    'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by'
  ]);
  
  return context
    .toLowerCase()
    .match(/\b\w{3,}\b/g) // Words with 3+ characters
    ?.filter(word => !stopWords.has(word))
    ?.slice(0, maxWords) || [];
};

/**
 * Generate SEO-optimized image title
 */
export const generateOptimizedImageTitle = (
  image: Partial<ImageSEOData>,
  targetKeywords: string[] = []
): string => {
  const { alt = '', caption = '', filename = '' } = image;
  
  // Use alt text as base, fallback to caption or filename
  let title = alt || caption || deriveAltFromFilename(filename);
  
  // Add primary keyword if not present
  const primaryKeyword = targetKeywords[0];
  if (primaryKeyword && !title.toLowerCase().includes(primaryKeyword.toLowerCase())) {
    title = `${title} - ${primaryKeyword}`;
  }
  
  // Ensure proper length
  if (title.length > 60) {
    title = title.substring(0, 57) + '...';
  }
  
  return title;
};

/**
 * Generate SEO-friendly filename
 */
export const generateSEOFilename = (
  originalFilename: string,
  altText: string,
  targetKeywords: string[] = []
): string => {
  // Extract extension
  const extension = originalFilename.split('.').pop() || 'jpg';
  
  // Use alt text as base for filename
  let filename = altText
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Remove multiple consecutive hyphens
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  
  // Add primary keyword if not present
  const primaryKeyword = targetKeywords[0];
  if (primaryKeyword && !filename.includes(primaryKeyword.toLowerCase().replace(/\s+/g, '-'))) {
    const keywordSlug = primaryKeyword.toLowerCase().replace(/\s+/g, '-');
    filename = `${keywordSlug}-${filename}`;
  }
  
  // Ensure reasonable length
  if (filename.length > 50) {
    filename = filename.substring(0, 50).replace(/-[^-]*$/, ''); // Cut at word boundary
  }
  
  return `${filename}.${extension}`;
};

/**
 * Analyze image SEO performance
 */
export const analyzeImageSEO = (
  image: ImageSEOData,
  targetKeywords: string[] = []
): ImageSEOAnalysis => {
  const issues: string[] = [];
  const recommendations: string[] = [];
  let score = 100;
  
  // Check alt text
  if (!image.alt) {
    issues.push('Missing alt text');
    score -= 30;
  } else {
    if (image.alt.length < 10) {
      issues.push('Alt text too short');
      score -= 15;
    }
    if (image.alt.length > 125) {
      issues.push('Alt text too long (over 125 characters)');
      score -= 10;
    }
    if (image.alt.toLowerCase().startsWith('image of') || 
        image.alt.toLowerCase().startsWith('picture of')) {
      issues.push('Alt text contains redundant prefix');
      score -= 5;
    }
  }
  
  // Check filename
  if (image.filename.match(/^(img|image|photo|pic)\d+\./i)) {
    issues.push('Generic filename not SEO-friendly');
    score -= 15;
  }
  
  // Check keywords
  const hasKeywordInAlt = targetKeywords.some(keyword => 
    image.alt?.toLowerCase().includes(keyword.toLowerCase())
  );
  if (targetKeywords.length > 0 && !hasKeywordInAlt) {
    issues.push('Alt text missing target keywords');
    score -= 20;
  }
  
  // Check file size (if available)
  if (image.fileSize && image.fileSize > 500000) { // 500KB
    issues.push('Image file size too large (over 500KB)');
    score -= 10;
  }
  
  // Check dimensions
  if (image.width && image.height) {
    if (image.width > 2000 || image.height > 2000) {
      issues.push('Image dimensions too large');
      score -= 5;
    }
  } else {
    issues.push('Missing width/height attributes');
    score -= 5;
  }
  
  // Check format
  if (!['webp', 'avif', 'jpg', 'jpeg', 'png'].includes(image.format.toLowerCase())) {
    issues.push('Unsupported or non-optimal image format');
    score -= 10;
  }
  
  // Generate recommendations
  if (issues.length === 0) {
    recommendations.push('Image SEO is well optimized!');
  } else {
    if (!image.alt) {
      recommendations.push('Add descriptive alt text that explains the image content');
    }
    if (targetKeywords.length > 0 && !hasKeywordInAlt) {
      recommendations.push(`Include target keyword "${targetKeywords[0]}" in alt text`);
    }
    if (image.filename.match(/^(img|image|photo|pic)\d+\./i)) {
      recommendations.push('Rename file with descriptive, keyword-rich filename');
    }
    if (image.fileSize && image.fileSize > 500000) {
      recommendations.push('Compress image to reduce file size');
    }
    if (!image.title) {
      recommendations.push('Add title attribute for additional SEO value');
    }
  }
  
  return {
    score: Math.max(0, score),
    issues,
    recommendations,
    optimizedAlt: generateOptimizedAltText(image, image.context, targetKeywords),
    optimizedTitle: generateOptimizedImageTitle(image, targetKeywords),
    optimizedFilename: generateSEOFilename(image.filename, image.alt || '', targetKeywords),
  };
};

/**
 * Generate image sitemap XML
 */
export const generateImageSitemap = (
  pages: Array<{
    url: string;
    images: ImageSEOData[];
  }>
): string => {
  const sitemapEntries: ImageSitemapEntry[] = pages.map(page => ({
    loc: page.url,
    image: page.images.map(img => ({
      loc: img.src,
      caption: img.caption,
      title: img.title || img.alt,
    })),
  }));
  
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"
        xmlns:image="http://www.google.com/schemas/sitemap-image/1.1">
${sitemapEntries.map(entry => `  <url>
    <loc>${entry.loc}</loc>
${entry.image.map(img => `    <image:image>
      <image:loc>${img.loc}</image:loc>
${img.caption ? `      <image:caption>${escapeXml(img.caption)}</image:caption>` : ''}
${img.title ? `      <image:title>${escapeXml(img.title)}</image:title>` : ''}
    </image:image>`).join('\n')}
  </url>`).join('\n')}
</urlset>`;
  
  return xml;
};

/**
 * Escape XML special characters
 */
const escapeXml = (text: string): string => {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

/**
 * Extract images from HTML content
 */
export const extractImagesFromContent = (
  htmlContent: string,
  baseUrl: string = ''
): ImageSEOData[] => {
  const imgRegex = /<img[^>]+>/gi;
  const images: ImageSEOData[] = [];
  let match;
  
  while ((match = imgRegex.exec(htmlContent)) !== null) {
    const imgTag = match[0];
    
    // Extract attributes
    const src = extractAttribute(imgTag, 'src') || '';
    const alt = extractAttribute(imgTag, 'alt') || '';
    const title = extractAttribute(imgTag, 'title');
    const width = parseInt(extractAttribute(imgTag, 'width') || '0') || undefined;
    const height = parseInt(extractAttribute(imgTag, 'height') || '0') || undefined;
    
    // Resolve relative URLs
    const fullSrc = src.startsWith('http') ? src : `${baseUrl}${src}`;
    
    // Extract filename and format
    const filename = src.split('/').pop() || '';
    const format = filename.split('.').pop()?.toLowerCase() || '';
    
    // Extract context (surrounding text)
    const contextStart = Math.max(0, match.index - 200);
    const contextEnd = Math.min(htmlContent.length, match.index + match[0].length + 200);
    const context = htmlContent.substring(contextStart, contextEnd)
      .replace(/<[^>]*>/g, ' ') // Remove HTML tags
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
    
    images.push({
      src: fullSrc,
      alt,
      title,
      filename,
      width,
      height,
      format,
      keywords: [], // Would be populated based on page keywords
      context,
    });
  }
  
  return images;
};

/**
 * Extract attribute value from HTML tag
 */
const extractAttribute = (tag: string, attribute: string): string | undefined => {
  const regex = new RegExp(`${attribute}=["']([^"']*)["']`, 'i');
  const match = tag.match(regex);
  return match ? match[1] : undefined;
};

/**
 * Optimize images in HTML content
 */
export const optimizeImagesInContent = (
  htmlContent: string,
  targetKeywords: string[] = [],
  baseUrl: string = ''
): { optimizedContent: string; changes: string[] } => {
  const images = extractImagesFromContent(htmlContent, baseUrl);
  const changes: string[] = [];
  let optimizedContent = htmlContent;
  
  images.forEach(image => {
    const analysis = analyzeImageSEO(image, targetKeywords);
    
    if (analysis.issues.length > 0) {
      // Create optimized img tag
      const originalTag = `<img[^>]*src=["']${image.src.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["'][^>]*>`;
      const regex = new RegExp(originalTag, 'gi');
      
      const optimizedTag = optimizedContent.match(regex)?.[0];
      if (optimizedTag) {
        let newTag = optimizedTag;
        
        // Update alt text if needed
        if (!image.alt || analysis.optimizedAlt !== image.alt) {
          if (image.alt) {
            newTag = newTag.replace(/alt=["'][^"']*["']/i, `alt="${analysis.optimizedAlt}"`);
          } else {
            newTag = newTag.replace(/<img/, `<img alt="${analysis.optimizedAlt}"`);
          }
          changes.push(`Updated alt text for ${image.filename}`);
        }
        
        // Add title if missing
        if (!image.title) {
          newTag = newTag.replace(/<img/, `<img title="${analysis.optimizedTitle}"`);
          changes.push(`Added title attribute for ${image.filename}`);
        }
        
        // Add width/height if missing
        if (!image.width || !image.height) {
          // Note: In a real implementation, you'd need to get actual dimensions
          newTag = newTag.replace(/<img/, '<img loading="lazy"');
          changes.push(`Added lazy loading for ${image.filename}`);
        }
        
        optimizedContent = optimizedContent.replace(optimizedTag, newTag);
      }
    }
  });
  
  return { optimizedContent, changes };
};

/**
 * Generate structured data for images
 */
export const generateImageStructuredData = (
  images: ImageSEOData[],
  pageUrl: string,
  pageTitle: string
): object => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ImageGallery',
    name: `Images from ${pageTitle}`,
    url: pageUrl,
    image: images.map(img => ({
      '@type': 'ImageObject',
      url: img.src,
      name: img.title || img.alt,
      description: img.alt,
      caption: img.caption,
      width: img.width,
      height: img.height,
      encodingFormat: `image/${img.format}`,
    })),
  };
};